/** 订单相关类型 */

/** 订单状态（与数据库 order_status 枚举 8 值一致） */
export enum OrderStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  SHIPPING = 'SHIPPING',
  EXPORT_CUSTOMS = 'EXPORT_CUSTOMS',
  IMPORT_CUSTOMS = 'IMPORT_CUSTOMS',
  DELIVERED = 'DELIVERED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/** 订单显示状态（UI 专用，7 种 V1 状态） */
export enum OrderDisplayStatus {
  TEMPERATURE_ALERT = 'TEMPERATURE_ALERT',     // 温度报警
  COMPLETED = 'COMPLETED',                       // 已完成
  IOT_OFFLINE = 'IOT_OFFLINE',                   // IoT离线
  DOCUMENTS_PENDING = 'DOCUMENTS_PENDING',       // 四证待校
  NODE_TIMEOUT = 'NODE_TIMEOUT',                 // 节点超时
  NORMAL = 'NORMAL',                             // 正常
  PENDING_SIGN = 'PENDING_SIGN',                 // 待签约
}

/** 柜映射信息 */
export interface ContainerMapping {
  containerCount: number;        // 映射柜数, 如 1, 2
  containerNos: string[];        // 柜号列表, 如 ["BLU-C-2612"]
}

/** 当前节点信息 */
export interface CurrentNode {
  nodeName: string;              // 节点名, 如 "备货", "装柜", "海运"
  color: string;                 // 圆点颜色, hex 值
}

/** 交易订单统计面板数据 */
export interface TradeOrderStats {
  monthlyOrderCount: number;         // 本月订单数, 如 28
  signedCount: number;               // 已签约数, 如 24
  pendingReviewCount: number;        // 待审核数, 如 4
  piComplianceRate: number;          // PI合规率(%), 如 96.4
  piPendingFixCount: number;         // PI待修正数, 如 1
  anomalyCount: number;              // 异常交易数, 如 2
  oversoldCount: number;             // 超卖预警数, 如 1
  docIncompleteCount: number;        // 四证不全数, 如 1
  totalContainerMappings: number;    // 订单-柜映射总数, 如 47
  multiOrderCount: number;           // 1:N订单数（一单多柜）, 如 6
  sharedContainerCount: number;      // 拼柜数, 如 2
}

/** 订单 */
export interface Order {
  id: string;
  orderNo: string;
  buyerId: string;
  buyerName: string;
  /** 主商品ID（关联 products 表） */
  productId?: string;
  productName: string;
  category: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  /** 标准单价（参考价） */
  standardUnitPrice?: number;
  /** 订单单价（实际成本价） */
  orderUnitPrice?: number;
  totalAmount: number;
  /** 成本总价 */
  totalCost?: number;
  /** 其他费用 */
  otherFees?: number;
  /** 最终总价（根据贸易术语计算） */
  finalTotal?: number;
  currency: string;
  /** 原始金额（人民币） */
  originalAmount?: number;
  /** 结算币种（USD/AED等） */
  settlementCurrency?: string;
  /** 使用汇率 */
  exchangeRate?: number;
  /** 汇率日期 */
  exchangeRateDate?: string;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  containerIds: string[];
  tradeTerms: string;
  originPort: string;
  destinationPort: string;
  /** V1 新增 — 规格, 如 "5.5cm+" */
  specification: string;
  /** V1 新增 — 航线, 如 "青岛 → 迪拜" */
  route: string;
  /** 航线 ID（逻辑引用 routes.id，无外键；下单时双写快照） */
  routeId?: string;
  /** V1 新增 — 柜映射信息 */
  containerMapping: ContainerMapping;
  /** V1 新增 — 当前节点信息（名称由后端 deriveCurrentNode 计算，存在缓存陈旧风险，前端应优先用 currentNodeCode + 实时节点配置映射） */
  currentNode: CurrentNode;
  /** 当前节点编号 (1-10)，用于柜档案关联 */
  currentNodeNumber: number;
  /** 当前节点代码（如 order/produce/ship/arrive），后端 /order/list 已返回；前端统一据此 + 实时 nodes 配置映射节点名称 */
  currentNodeCode?: string;
  /** 付款方式 */
  paymentMethod: string;
  /** 付款拆分（T/T时使用） */
  paymentSplit?: PaymentSplit;
  /** 预计离港 ETD */
  etd: string;
  /** 预计到港 ETA */
  eta: string;
  /** V1 新增 — UI 显示状态 */
  displayStatus: OrderDisplayStatus;
  /** 唛头信息 */
  shippingMark?: ShippingMark;

  // --- 新增字段（采购成本/运输/贸易术语明细/结算）---
  /** 采购单价(CNY) */
  purchaseUnitPrice?: number;
  /** 利润点(%) */
  profitMargin?: number;
  /** 商品销售价格(CNY) */
  sellingPrice?: number;
  /** 船公司 */
  shippingCompany?: string;
  /** 海运费(USD) */
  oceanFreight?: number;
  /** 预计航行天数 */
  estimatedSailingDays?: number;
  /** 国内运输(CNY) */
  domesticTransport?: number;
  /** 港杂费(CNY) */
  portMiscFee?: number;
  /** 保险费(CNY) */
  insuranceFee?: number;
  /** 清关费(CNY) */
  customsClearanceFee?: number;
  /** 税费(CNY) */
  taxes?: number;
  /** 目的国内陆派送费(CNY) */
  inlandDeliveryFee?: number;
  /** 最终总价(CNY) */
  finalTotalPrice?: number;
  /** 最终单价(CNY) */
  finalUnitPrice?: number;
  /** 结算金额 */
  settlementAmount?: number;
  /** OA 赊销账期天数 */
  oaCreditDays?: number;
  /** 付款备注 */
  paymentNote?: string;
}

/** 订单列表查询参数（前端 → GET /order/list） */
export interface OrderListQuery {
  page: number;
  pageSize?: number;
  orderNo?: string;
  buyerName?: string;
  category?: string;
  route?: string;
  /** 航线 ID 筛选（GET /order/list） */
  routeId?: string;
  status?: string;
  displayStatus?: string;
}

/** 唛头（Shipping Mark） */
export interface ShippingMark {
  required: boolean;
  text: string;
  logoUrl: string;
  pieces: number;
  netWeight: number;   // 净重 KG
  grossWeight: number; // 毛重 KG
  destination: string;
  orderNo: string;
  origin: string;
}

/** 付款拆分（T/T 电汇） */
export interface PaymentSplit {
  depositPercent: number;   // 定金比例，如 30
  balancePercent: number;   // 尾款比例，如 70
  depositAmount: number;    // 定金金额 USD
  balanceAmount: number;    // 尾款金额 USD
}

/** 商品类目
 * @deprecated 请使用 types/product.ts 中的 CategoryNode + ProductDetail 替代
 */
export interface ProductCategory {
  id: string;
  name: string;
  code: string;
  parentId: string | null;
  description: string;
  activeOrders: number;
  createdAt: string;
}

/** 船务订舱 */
export interface ShippingBooking {
  id: string;
  bookingNo: string;
  orderId: string;
  orderNo: string;
  containerNo: string;
  vesselName: string;
  voyageNo: string;
  departurePort: string;
  arrivalPort: string;
  departureDate: string;
  arrivalDate: string;
  carrier: string;
  status: string;
  createdAt: string;
}

// ===== D03 报关与清关管理 =====

/** 商检状态 */
export type InspectionStatus = 'PASSED' | 'PENDING' | 'FAILED';

/** 植检状态 */
export type PhytoStatus = 'PASSED' | 'PENDING' | 'FAILED';

/** 海关申报状态 */
export type DeclarationSubmitStatus = 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'NOT_SUBMITTED';

/** 放行状态 */
export type ReleaseStatus = 'RELEASED' | 'PENDING_RELEASE' | 'HELD';

/** 清关步骤状态 */
export type ClearanceStepStatus = 'DONE' | 'CURRENT' | 'PENDING';

/** 资料状态 */
export type DocumentStatus = 'READY' | 'PENDING' | 'MISSING';

/** 已上传的报关资料附件 */
export interface UploadedCustomsDoc {
  /** 文件名称 */
  name: string;
  /** 文件 data URL（用于预览） */
  dataUrl: string;
  /** 是否为图片（用于判断预览方式） */
  isImage: boolean;
  /** 上传时间 */
  uploadDate: string;
}

/** 出口报关（主表） */
export interface CustomsDeclaration {
  id: string;
  orderNo: string;
  containerNo: string;
  category: string;
  declarationNo: string;
  inspectionStatus: InspectionStatus;
  phytoStatus: PhytoStatus;
  phytoDate: string;
  customsDeclarationStatus: DeclarationSubmitStatus;
  releaseStatus: ReleaseStatus;
  destinationClearanceId: string;
  /** 已上传的报关资料附件（key 为资料名称） */
  uploadedDocuments?: Record<string, UploadedCustomsDoc>;
}

/** 清关步骤 */
export interface ClearanceStep {
  id: string;
  name: string;
  description?: string;
  status: ClearanceStepStatus;
  timestamp: string;
}

/** 目的港清关详情 */
export interface DestinationClearance {
  id: string;
  declarationId: string;
  orderNo: string;
  containerNo: string;
  destinationPort: string;
  eta: string;
  steps: ClearanceStep[];
  alertInfo: string;
}

/** 报关资料 */
export interface ClearanceDocument {
  id: string;
  declarationId: string;
  docType: string;
  docName: string;
  status: DocumentStatus;
  uploadDate: string;
}

/** 商检/植检记录 */
export interface InspectionRecord {
  id: string;
  orderNo: string;
  containerNo: string;
  category: string;
  inspectionType: string;
  status: InspectionStatus;
  inspectionDate: string;
  certificateNo: string;
  result: string;
  agency: string;
}

/** 报关清关（旧版）
 * @deprecated 请使用 CustomsDeclaration、DestinationClearance、InspectionRecord 替代
 */
export interface CustomsClearance {
  id: string;
  declarationNo: string;
  orderId: string;
  orderNo: string;
  containerNo: string;
  customsOffice: string;
  declarationType: string;
  status: string;
  submittedAt: string;
  clearedAt: string;
  agentName: string;
  notes: string;
}

/** 单证校验 */
export interface DocumentVerification {
  id: string;
  orderId: string;
  orderNo: string;
  documentType: string;
  documentNo: string;
  status: string;
  verifiedBy: string;
  verifiedAt: string;
  issues: string[];
  fileUrls: string[];
}

/** 理赔规则 */
export interface ClaimRule {
  id: string;
  name: string;
  category: string;
  coverageAmount: number;
  currency: string;
  deductible: number;
  conditions: string;
  isActive: boolean;
  createdAt: string;
}

/** 风控评级（旧版评分卡）
 * @deprecated 使用 BuyerCreditRating 替代（D08 授信额度模型）
 */
export interface RiskRating {
  id: string;
  buyerId: string;
  buyerName: string;
  overallScore: number;
  creditScore: number;
  tradeScore: number;
  complianceScore: number;
  rating: string;
  lastEvaluated: string;
  remarks: string;
}

// ===== D08 风控与买家评级 =====

/** 授信评级（A/B/C 三级，替代旧 AAA-BBB 评分卡） */
export type CreditRating = 'A' | 'B' | 'C';

/** 授信状态 */
export type CreditStatus = 'active' | 'watch' | 'frozen';

/** 拦截原因 */
export type InterceptReason = 'insufficient_credit' | 'overdue';

/** 损益类别 */
export type PnLCategory =
  | 'cif_revenue'
  | 'procurement_cost'
  | 'fulfillment_cost'
  | 'blu_guard_premium'
  | 'blu_credit_interest'
  | 'iot_depreciation'
  | 'gross_profit'
  | 'net_profit';

/** 买家授信评级（替代旧 RiskRating） */
export interface BuyerCreditRating {
  id: string;                     // "BCR-001"
  buyerId: string;                // "BUY001"
  buyerName: string;              // "FreshFoods Inc."
  rating: CreditRating;           // A / B / C
  creditLimit: number;            // 授信额度
  inTransitOccupation: number;    // 在途占用金额
  availableCredit: number;        // 可用额度 = creditLimit - inTransitOccupation
  overdueRecords: number;         // 逾期记录数
  status: CreditStatus;           // active / watch / frozen
  lastUpdated: string;            // ISO 8601
}

/** 拦截记录 */
export interface InterceptRecord {
  id: string;                     // "INT-001"
  buyerId: string;                // "BUY001"
  buyerName: string;              // "FreshFoods Inc."
  reason: InterceptReason;        // insufficient_credit | overdue
  orderNo: string;                // 关联订单号 "ORD-089"
  amount: number;                 // 申请/涉及金额
  creditShortfall: number;        // 额度缺口（insufficient_credit 时有效）
  timestamp: string;              // ISO 8601
  status: 'pending' | 'resolved'; // 处理状态
}

/** 损益表项 */
export interface ProfitLossItem {
  id: string;                     // "PL-001"
  category: PnLCategory;          // 损益类别
  label: string;                  // 显示标签 "CIF收入"
  amount: number;                 // 金额（正值=收入, 负值=成本/费用）
  isSubtotal?: boolean;           // 是否为小计行（毛利）
  isTotal?: boolean;              // 是否为合计行（净利润）
  note?: string;                  // 备注 "18.1%"
  indent?: number;                // 缩进层级（0=顶级, 1=子项）
}

/** 风控统计面板数据 */
export interface CreditStats {
  totalCredit: number;              // BluCredit总额 5,000,000
  usedCredit: number;               // 已用额度 1,430,000
  availableCredit: number;          // 可用额度 3,570,000
  usageRate: number;                // 使用率 28.6
  interceptCount: number;           // 拦截记录总数 3
  insufficientCreditCount: number;  // 额度不足拦截数 2
  overdueInterceptCount: number;    // 逾期拦截数 1
  riskReserve: number;              // 风险准备金 410,000
  provisionRate: number;            // 计提比例 5%
  alertThreshold: number;           // 警戒线 300,000
}

/** 趋势点 */
export interface TrendPoint {
  date: string;
  orders: number;
  revenue: number;
}

/** 航线统计 */
export interface RouteStat {
  route: string;
  count: number;
  percentage: number;
}

// ===== D02 船务与订舱管理 =====

/** 船期状态 */
export enum ScheduleStatus {
  CLOSING = 'CLOSING',
  AVAILABLE = 'AVAILABLE',
  PLENTY = 'PLENTY',
}

/** 船期（主表） */
export interface ShipSchedule {
  id: string;
  vesselName: string;
  voyageNo: string;
  departurePort: string;
  arrivalPort: string;
  etd: string;
  eta: string;
  cutoffDate: string;
  vgmCutoff: string;
  totalSlots: number;
  bookedSlots: number;
  status: ScheduleStatus;
  carrier: string;
  shippingCompany: string;
}

/** 关联柜档案 */
export interface LinkedContainer {
  containerNo: string;
  archiveNo: string;
  type: string;
}

/** 订舱详情 */
export interface BookingDetail {
  id: string;
  soNumber: string;
  shippingCompany: string;
  forwarder: string;
  containerType: string;
  containerCount: number;
  etd: string;
  eta: string;
  cutoffDate: string;
  vgmCutoff: string;
  linkedContainers: LinkedContainer[];
}

/** 船期筛选参数 */
export interface ShipScheduleFilter {
  departurePort: string;
  arrivalPort: string;
}

/** 统计面板数据 */
export interface ShippingStats {
  monthlySailings: number;
  bookedSailings: number;
  pendingSailings: number;
  urgentCutoffs: number;
  vesselsInPort: number;
  vesselNamesInPort: string[];
  avgVoyageDays: number;
  onTimeRate: number;
}

// ===== D07 BluGuard 理赔规则引擎 =====

/** 理赔类型 */
export type ClaimType = 'temperature_exceeded' | 'iot_offline' | 'cargo_damage' | 'shortage';

/** 定责结果 */
export type LiabilityResult = 'assessing' | 'pending_liability' | 'carrier_60' | 'platform_100' | 'pre_loading_40';

/** 工单状态（处理中/定损中/审批中/待赔付/已结案-赔付/已结案-拒赔） */
export type ClaimWorkOrderStatus =
  | 'processing'
  | 'assessing'
  | 'approving'
  | 'pending_payout'
  | 'settled_paid'
  | 'settled_rejected';

/** 证据状态 */
export type EvidenceStatus = 'normal' | 'warning' | 'danger';

/** IoT证据 */
export interface IotEvidence {
  id: string;
  timestamp: string;
  eventType: string;
  description: string;
  deviceNo: string;
  readingValue: string;
  status: EvidenceStatus;
}

/**
 * 理赔流转日志动作类型（对应 claim_status_logs.action）。
 *
 * - `ROLLBACK`：打回上一态（严格回退一步，可再次推进），与终态动作 `REJECT` 并存
 * - `FIELD_UPDATE`：文本字段级 diff（描述 / 船名 / 航次 / 操作人），明细见 `changedFields`
 */
export type ClaimLogAction =
  | 'CREATE'
  | 'ADVANCE'
  | 'REJECT'
  | 'ROLLBACK'
  | 'AMOUNT_UPDATE'
  | 'LIABILITY_UPDATE'
  | 'FIELD_UPDATE';

/**
 * 字段级变更明细（`ClaimStatusLog.changedFields` 反序列化后的单项）。
 *
 * <p>后端以 JSON 字符串存 text 列（非 jsonb），前端用 `parseChangedFields` 容错解析。
 * `oldValue` / `newValue` 为<b>用户输入</b>，渲染进 `dangerouslySetInnerHTML`
 * 前必须经 `escapeHtml()` 转义。
 */
export interface ClaimFieldDiff {
  /** 字段名（驼峰，如 `description`） */
  field: string;
  /** 中文字段名快照（后端写入时固化，避免前端漏映射） */
  label: string;
  oldValue?: string | null;
  newValue?: string | null;
}

/** 驳回原因码（6 值固定白名单，与后端 ClaimRejectDTO.REASON_CODES 严格一致） */
export type ClaimRejectReason =
  | 'insufficient_evidence'
  | 'not_carrier_liability'
  | 'duplicate_report'
  | 'customer_withdrawn'
  | 'out_of_coverage'
  | 'other';

/** 理赔状态流转日志（对应 claim_status_logs，只增不改不删） */
export interface ClaimStatusLog {
  id: string;
  claimId: string;
  seq: number;
  action: ClaimLogAction;
  fromStatus?: ClaimWorkOrderStatus | null;
  toStatus?: ClaimWorkOrderStatus | null;
  amountBefore?: number | null;
  amountAfter?: number | null;
  /** 金额变更时的币种快照 */
  currency?: string | null;
  liabilityBefore?: LiabilityResult | null;
  liabilityAfter?: LiabilityResult | null;
  operatorId?: string | null;
  operatorName?: string | null;
  /** 驳回 / 打回原因码（取自数据字典 `claim_reject_reason`，两个动作共用该列） */
  rejectReasonCode?: ClaimRejectReason | null;
  /** 用户输入，渲染进 dangerouslySetInnerHTML 前必须 HTML 转义 */
  remark?: string | null;
  /**
   * 字段级变更明细的 **JSON 字符串**（后端 text 列原样透传，可能为 null / 非法 JSON）。
   * 用 `parseChangedFields()` 解析，勿直接 `JSON.parse`。
   */
  changedFields?: string | null;
  /** 操作 IP（合规留痕，仅管理端展示） */
  ipAddress?: string | null;
  /** 操作设备 User-Agent 原文（合规留痕，仅管理端展示） */
  userAgent?: string | null;
  createdAt: string;
}

/** 理赔工单 */
export interface ClaimWorkOrder {
  id: string;
  clmNo: string;
  orderNo: string;
  claimType: ClaimType;
  claimAmount: number;
  currency: string;
  liabilityResult: LiabilityResult;
  status: ClaimWorkOrderStatus;
  createdAt: string;
  updatedAt: string;
  operatorName: string;
  containerNo: string;
  vesselName: string;
  voyageNo: string;
  description: string;
  iotEvidences: IotEvidence[];
  /** 关联告警 ID（告警一键建单时存在，人工建单为空）——温度曲线按钮的可用判据 */
  alertId?: string;
  /** 状态流转日志（created_at 升序，历史单为空数组） */
  statusLogs?: ClaimStatusLog[];
  /** 分段定责数据 */
  liabilitySegments?: Array<{ label: string; percentage: number; color?: string }>;
}

/** 理赔统计 */
export interface ClaimStats {
  processingCount: number;
  assessingCount: number;
  approvingCount: number;
  pendingPayoutCount: number;
  monthlySettledCount: number;
  monthlyPaidCount: number;
  monthlyRejectedCount: number;
  totalPayoutAmount: number;
  avgResponseTime: number;
}

/** 定责分段 */
export interface LiabilitySegment {
  label: string;
  percentage: number;
  isCurrent: boolean;
  description: string;
  color: string;
}

/** 理赔规则配置数据 */
export interface ClaimRuleConfigData {
  tempUpperThreshold: number;
  durationHours: number;  // 超温持续时间阈值，单位小时
  deductible: number;
  payoutRatio: number;
  iotLiabilitySegments: LiabilitySegment[];
}
